import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useLocalizedPath } from '@/lib/i18n/useLocale';
import MainHeading from '@/components/heading/MainHeading';
import { topRowProductCards, type ProductCardConfig } from '../../data/configs.data';
import { motion } from 'framer-motion';
import { ArrowLeft } from 'lucide-react';
import type { ComponentType } from 'react';

function ProductIcon({ icon }: { icon: string | ComponentType }) {
    if (typeof icon === 'string') {
        return (
            <img src={icon} alt="" className="h-[120px] w-auto max-w-full object-contain sm:h-[140px]" loading="lazy" />
        );
    }

    const Icon = icon;

    return (
        <div className="flex h-[120px] w-full items-center justify-center sm:h-[140px] [&_svg]:h-full [&_svg]:max-h-[120px] [&_svg]:w-auto sm:[&_svg]:max-h-[140px]">
            <Icon />
        </div>
    );
}

function ProductCard({ card, index, to, from }: { card: ProductCardConfig; index: number; to: string; from: string }) {
    const { t } = useTranslation();

    return (
        <Link to={to} state={{ from }} className="group block h-full">
            <motion.article
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="flex h-full flex-col items-center overflow-hidden rounded-2xl border border-primary-cardborder px-5 py-8 text-center shadow-[0px_2px_4px_-2px_rgba(16,24,40,0.06),0px_4px_8px_-2px_rgba(16,24,40,0.1)] transition-shadow duration-300 group-hover:shadow-[0px_4px_8px_-2px_rgba(16,24,40,0.12),0px_8px_16px_-4px_rgba(16,24,40,0.14)] dark:shadow-[0px_4px_4px_-1px_rgba(12,12,13,0.05),0px_4px_4px_-1px_rgba(12,12,13,0.1)] dark:group-hover:shadow-[0px_4px_4px_-1px_rgba(12,12,13,0.08),0px_4px_4px_-1px_rgba(12,12,13,0.16)] sm:px-6 sm:py-10"
                style={{
                    background:
                        'radial-gradient(ellipse 85% 75% at 0% 0%, var(--products-card-glow), transparent 68%), var(--products-card-base)',
                }}
            >
                <ProductIcon icon={card.Icon} />

                <h3 className="mt-5 font-semibold font-alexandria text-primary-cardtxt text-[15px] sm:text-base lg:text-lg leading-[1.45]">
                    {t(card.titleKey)}
                </h3>

                <p className="mt-3 text-center max-w-[350px] mx-auto font-cairo font-normal text-primary-carddec text-[13px] sm:text-[14px] lg:text-[16px] leading-[1.6]">
                    {t(card.descriptionKey)}
                </p>

                {card.readMore && (
                    <span className="mt-4 inline-flex items-center gap-2 font-cairo text-[#535862] text-sm underline underline-offset-[3px] decoration-[#535862]/90">
                        {t('servicesPage.readMore')}
                        <ArrowLeft className="h-3.5 w-3.5 shrink-0 stroke-[2.25] rtl:rotate-180" aria-hidden />
                    </span>
                )}
            </motion.article>
        </Link>
    );
}

export default function ProductsCard() {
    const { t } = useTranslation();
    const localizedPath = useLocalizedPath();
    const location = useLocation();

    return (
        <section className="md:my-[140px] sm:my-[120px] my-[90px]">
            <MainHeading>{t('home.products.title')}</MainHeading>

            <div className="mt-10 flex flex-col gap-5 sm:gap-6">
                <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 sm:gap-6 lg:grid-cols-3">
                    {topRowProductCards.map((card, index) => (
                        <ProductCard
                            key={card.id}
                            card={card}
                            index={index}
                            to={card.path(localizedPath)}
                            from={location.pathname}
                        />
                    ))}
                </div>
            </div>
        </section>
    );
}
